import { Button, Container, Form, Typography } from '@app/components';
import { FormStepper } from '@app/components/stepper/FormStepper';
import { useHandler } from '@app/hooks/useHandler.hook';
import { clsxm } from '@app/styles/clsxm';
import { useEffect, useMemo, useState } from 'react';
import { FormProvider, SubmitErrorHandler, SubmitHandler, useForm } from 'react-hook-form';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { SelectQRCodeTypeContainer } from '@app/domain/qr-code/components/type/SelectQRCodeTypeContainer';
import { QRCodeFormSchema } from '@app/domain/qr-code/api/qr-code.form.schema.api';
import { yupResolver } from '@hookform/resolvers/yup';
import { ECodeType, LinkInDto, QRCodeInDto, VCardLinkInDto } from '@app/swagger-types';
import { QRCodeInputContainer } from '@app/domain/qr-code/components/input/QRCodeInputContainer';
import { QRCodeCustomizeContainer } from '@app/domain/qr-code/components/customize/QRCodeCustomizeContainer';
import { DEFAULT_TEMPLATE_FORM_VALUES } from '@app/domain/template/constants';
import { QRCodeDoneStep } from '@app/domain/qr-code/components/done/QRCodeDoneStep';
import { toast } from 'react-toastify';
import { isKeyInObject } from '@app/utils/object.utils';
import { QR_CODE_DONE_STEP_FORM_FIELDS, QR_CODE_INPUT_STEP_FORM_FIELDS } from '@app/domain/qr-code/constants';
import { QRCodePreviewSection } from '@app/domain/qr-code/components/QRCodePreview.section';
import { useCreateBulkQRCodes, useCreateQRCode, useUpdateQRCode } from '@app/domain/qr-code/api/qr-code.hooks.api';
import { useTypedNavigate } from '@app/router';
import { Routes } from '@app/constants/routes';
import { pick } from 'lodash';
import { DesignFormSchema } from '@app/domain/template/api/template.form.schema.api';
import { NO_LOGO_ID } from '@app/components/gallery-editor/components/image-item/ImageItem';
import { BackButton } from '@app/components/buttons/back-button/BackButton';
import { QRCodeInDtoOverride } from '@app/swagger-override-types';
import { useTranslation } from 'react-i18next';
import { PlanLimitTooltip } from '@app/domain/plan/components/PlanLimitTooltip';
import { usePlanLimits } from '@app/domain/plan/hooks/usePlanLimits';
import { QRCodeBulk } from './input/bulk/QRCodeBulk';
import { EBulkMode, useBulkQueryParam } from '@app/domain/plan/components/bulk-button/useBulkQueryParam.hook';
import {
  EQRCodeStep,
  ESettingsTab,
  QR_CODE_NAVIGATION_QUERY_PARAM,
  SETTINGS_TAB_QUERY_PARAM,
  useQRCodeNavigation,
} from '../hooks/useQRCodeNavigation';
import { TranslationKey } from '@app/locales/defaultTranslation';
import { UpgradeBanner } from '@app/domain/plan/components/UpgradeBanner';
import { EPlanLimitType } from '@app/domain/plan/components/plan-limit-dialog/PlanLimitDialog';
import { GearIcon } from '@app/assets';
import { QRCodeStepper } from './QRCodeStepper';
import { QRSettingsCurrentTab } from './settings/QRSettingsCurrentTab';
import { COMMAND_EFFECT_REQUEST } from './qr-code-form.constants';
import { QRSettingsGlobalTab } from './settings/QRSettingsGlobalTab';
import { CsvParseResult, handleFileDrop } from './input/bulk/file-utils';

export enum EQRCodeSettingsStep {
  CURRENT = 'CURRENT',
  GLOBAL = 'GLOBAL',
}

const settingsSteps: TranslationKey[] = ['qr.settings.currentTab', 'qr.settings.globalTab'];

export const QRCodeForm: React.FC<{
  isCreateMode?: boolean;
  defaultValues: QRCodeFormSchema & { id?: string };
}> = ({ defaultValues, isCreateMode }) => {
  const { t } = useTranslation();
  const [bulkMode, setBulkMode] = useBulkQueryParam(EBulkMode.NONE);
  const { step, settingsTab, setQueryParams } = useQRCodeNavigation();
  const isInputStep = step === EQRCodeStep.INPUT;
  const isBulkMode = bulkMode === EBulkMode.BULK_WEBSITE;
  const QRCodeStepperSteps = isBulkMode
    ? [EQRCodeStep.TYPE, EQRCodeStep.BULK, EQRCodeStep.CUSTOMIZE, EQRCodeStep.DONE]
    : [EQRCodeStep.TYPE, EQRCodeStep.INPUT, EQRCodeStep.CUSTOMIZE, EQRCodeStep.DONE];
  const [activeStep, setActiveStep] = useState<EQRCodeStep>(step);
  const { isLoading: isLoadingBulk, mutate: createBulkQRCodes } = useCreateBulkQRCodes();

  const [completedSteps, setCompletedSteps] = useState<EQRCodeStep[]>(!isInputStep ? [] : [EQRCodeStep.TYPE]);

  const [fileData, setFileData] = useState<CsvParseResult>({
    file: null,
    data: null,
    type: null,
    error: null,
  });
  const isBulkFillCorrectly = Boolean(isBulkMode && fileData.data && !fileData.error && fileData.type);

  const { mutate: createQRCode, isLoading } = useCreateQRCode();
  const mutationForUpdate = useUpdateQRCode();
  const { mutate: updateQRCode, isLoading: updateLoading } = mutationForUpdate;

  const navigate = useTypedNavigate();

  const formMethods = useForm<QRCodeFormSchema>({
    resolver: yupResolver(QRCodeFormSchema),
    defaultValues,
  });

  const sendRequest = useHandler((formData: QRCodeInDtoOverride) => {
    if (isCreateMode) {
      createQRCode(formData, {
        onSuccess: () => {
          toast.success(t('toaster.qrCreated'));
          navigate({ to: Routes.customer.my_qr_codes.index });
        },
      });
    } else if (defaultValues?.id) {
      updateQRCode(
        { id: defaultValues.id, ...formData },
        {
          onSuccess: () => {
            toast.success(t('toaster.qrUpdated'));
            navigate({ to: Routes.customer.my_qr_codes.index });
          },
        }
      );
    }
  });

  const { control, handleSubmit, trigger, watch, getValues, reset } = formMethods;
  const { codeType } = watch();

  const onFileDrop = useHandler(async (file: File) => {
    if (!fileData?.type || !fileData?.data) {
      return;
    }
    const qrValues = getValues();
    const { data, type, error } = await handleFileDrop(file, fileData.type, qrValues);
    setFileData({
      file,
      data,
      type,
      error,
    });
  });

  const onSubmitSingle: SubmitHandler<QRCodeFormSchema> = useHandler((data) => {
    if (!canSaveQRCode) {
      return;
    }

    const formData = buildFormData(data);
    const contentDto = buildContentDto(data);

    if (Object.keys(contentDto).length === 0) {
      toast.warning('API in progress');
      return;
    }

    const codeDto: QRCodeInDto = {
      ...formData,
      ...contentDto,
      templateId: formData.templateId ?? undefined,
      password: formData.password ?? undefined,
    };

    sendRequest(codeDto);
  });

  const onSubmitBulk = () => {
    if (!canSaveQRCode || !fileData.data) {
      return;
    }

    const bulkDtos: QRCodeInDto[] = fileData.data.map((data) => ({
      ...buildFormData(data),
      ...buildContentDto(data),
      templateId: data.templateId ?? undefined,
      password: data.password ?? undefined,
    }));

    createBulkQRCodes(bulkDtos, {
      onSuccess: () => {
        toast.success(t('toaster.csvQRCreated'));
        reset();
        navigate({ to: Routes.customer.my_qr_codes.index });
      },
    });
  };

  const onErrorSubmit: SubmitErrorHandler<QRCodeFormSchema> = useHandler((errors) => {
    const inputStepHasErrors = isKeyInObject(QR_CODE_INPUT_STEP_FORM_FIELDS, errors);
    const doneStepHasErrors = isKeyInObject(QR_CODE_DONE_STEP_FORM_FIELDS, errors);

    if (inputStepHasErrors) {
      if (doneStepHasErrors) {
        setCompletedSteps((prev) => [...new Set([...prev.filter((s) => s !== EQRCodeStep.DONE)])]);
      } else {
        setCompletedSteps((prev) => [...new Set([...prev, EQRCodeStep.DONE])]);
      }

      setActiveStep(EQRCodeStep.INPUT);
    } else if (doneStepHasErrors) {
      if (inputStepHasErrors) {
        setCompletedSteps((prev) => [...new Set([...prev.filter((s) => s !== EQRCodeStep.INPUT)])]);
      } else {
        setCompletedSteps((prev) => [...new Set([...prev, EQRCodeStep.INPUT])]);
      }

      setCompletedSteps((prev) => [...new Set([...prev.filter((s) => s !== EQRCodeStep.DONE)])]);
      setActiveStep(EQRCodeStep.DONE);
    }

    return;
  });

  const onChangeActiveStep = useHandler(async (step: EQRCodeStep, skipValidation?: boolean) => {
    if (step === activeStep) {
      return;
    }

    const currentStepIndex = QRCodeStepperSteps.findIndex((s) => s === activeStep);

    if (isBulkMode && step === EQRCodeStep.TYPE) {
      setBulkMode(undefined);
    }

    if (!skipValidation) {
      if (QRCodeStepperSteps[currentStepIndex] === EQRCodeStep.INPUT) {
        const isInvalidInputStep = await trigger(QR_CODE_INPUT_STEP_FORM_FIELDS, { shouldFocus: true });
        if (!isInvalidInputStep) {
          setCompletedSteps((prev) => [...new Set(prev.filter((s) => s !== QRCodeStepperSteps[currentStepIndex]))]);
          return;
        }
      } else if (QRCodeStepperSteps[currentStepIndex] === EQRCodeStep.BULK) {
        if (!isBulkFillCorrectly) {
          setCompletedSteps((prev) => [...new Set(prev.filter((s) => s !== QRCodeStepperSteps[currentStepIndex]))]);
          return;
        }
      } else if (QRCodeStepperSteps[currentStepIndex] === EQRCodeStep.DONE && !isBulkMode) {
        const isValidDoneStep = await trigger('name', { shouldFocus: true });

        if (!isValidDoneStep) {
          setCompletedSteps((prev) => [...new Set(prev.filter((s) => s !== QRCodeStepperSteps[currentStepIndex]))]);
          return;
        }
      }

      setCompletedSteps((prev) => [...new Set([...prev, QRCodeStepperSteps[currentStepIndex]])]);
    }

    setActiveStep(step);
  });

  const getStepTitleKey = (step: EQRCodeStep): TranslationKey => {
    const stepTitles: Record<EQRCodeStep, TranslationKey> = {
      [EQRCodeStep.TYPE]: 'qr.stepType.stepTitle',
      [EQRCodeStep.INPUT]: 'qr.stepInput.stepTitle',
      [EQRCodeStep.BULK]: 'qr.stepBulk.stepTitle',
      [EQRCodeStep.CUSTOMIZE]: 'qr.stepCustomize.stepTitle',
      [EQRCodeStep.DONE]: 'qr.stepDone.stepTitle',
    };

    if (stepTitles[step]) {
      return stepTitles[step];
    }

    throw new Error(`Unknown step type: ${step}`);
  };

  const renderActiveStep = useMemo(() => {
    const isEditMode = !isCreateMode;
    switch (activeStep) {
      case EQRCodeStep.TYPE:
        return <SelectQRCodeTypeContainer isEditMode={isEditMode} />;
      case EQRCodeStep.INPUT:
        return <QRCodeInputContainer isEditMode={isEditMode} />;
      case EQRCodeStep.BULK:
        return (
          <QRCodeBulk
            fileData={fileData}
            setFileData={setFileData}
            onCancel={() => {
              setBulkMode(EBulkMode.NONE);
            }}
          />
        );
      case EQRCodeStep.CUSTOMIZE:
        return <QRCodeCustomizeContainer />;
      case EQRCodeStep.DONE:
        return (
          <QRCodeDoneStep
            qrCodes={
              fileData.data
                ? Object.values(fileData.data).map((value) => ({
                    ...value,
                    id: '',
                    name: typeof value.name === 'string' ? value.name : '',
                    website: {
                      url: value.websiteUrl,
                    },
                    shouldAskForGPSCoordinates: false,
                    createdDate: new Date().toISOString(),
                  }))
                : undefined
            }
          />
        );
      default:
        return null;
    }
  }, [activeStep, isCreateMode, setBulkMode, fileData]);

  const [isSettingsOpen, setIsSettingsOpen] = useState(!!settingsTab);
  const [settingsStep, setSettingsStep] = useState(() => {
    if (settingsTab === ESettingsTab.GLOBAL) {
      return 1;
    }
    return 0;
  });

  const openSettings = () => {
    setIsSettingsOpen((prev) => !prev);
  };

  useEffect(() => {
    if (!isCreateMode) {
      if (step !== activeStep) {
        setQueryParams({ [QR_CODE_NAVIGATION_QUERY_PARAM]: activeStep }, 'replaceIn');
      }
      if (!!settingsStep !== !!(settingsTab === ESettingsTab.GLOBAL ? 1 : 0)) {
        setQueryParams(
          { [SETTINGS_TAB_QUERY_PARAM]: settingsStep ? ESettingsTab.GLOBAL : ESettingsTab.CURRENT },
          'replaceIn'
        );
      }
    }
  }, [activeStep, step, setQueryParams, settingsStep, settingsTab, isSettingsOpen, isCreateMode]);

  useEffect(() => {
    if (isBulkMode) {
      setCompletedSteps((prev) => [...new Set([...prev.filter((s) => s !== EQRCodeStep.DONE), EQRCodeStep.TYPE])]);

      setActiveStep(EQRCodeStep.BULK);
    }
  }, [isBulkMode, setBulkMode]);

  useEffect(() => {
    if (activeStep === EQRCodeStep.DONE && isBulkMode && fileData.file) {
      onFileDrop(fileData.file);
    }
  }, [activeStep, isBulkMode, onFileDrop, fileData.file]);

  const { canCreateQRCode, canEditQrInput } = usePlanLimits();

  const checkIfActionButtonAvailable = (): boolean => {
    if (isBulkMode) {
      return isBulkFillCorrectly;
    }
    if (isCreateMode) {
      return canCreateQRCode || false;
    }
    if (isInputStep) {
      return canEditQrInput;
    }
    return true;
  };
  const canSaveQRCode = checkIfActionButtonAvailable();

  const showPreviewForVcard = codeType === ECodeType.V_CARD && activeStep === EQRCodeStep.INPUT;

  // TODO: intagrate with Edit QR Code
  const formTitle = isBulkMode ? t('qr.createBulk') : isCreateMode ? t('qr.createQRCode') : t('qr.editQRCode');
  const isDoneStep = activeStep === EQRCodeStep.DONE;

  return (
    <FormProvider {...formMethods}>
      <Form
        control={control}
        className={clsxm('flex h-full flex-col gap-4 pb-8 xl:h-auto xl:flex-row xl:px-6 xl:pb-6')}
        onSubmit={handleSubmit(onSubmitSingle, onErrorSubmit)}
      >
        <div className="flex flex-1 flex-col gap-4">
          {isCreateMode && !canCreateQRCode && <UpgradeBanner planLimitDialogType={EPlanLimitType.CREATE_QR_CODE} />}
          <Container className="xl:rounded-xl">
            <div className="mb-6 flex items-center justify-between">
              <div className="flex items-center ">
                {!isBulkMode && (
                  <BackButton
                    onClick={() => {
                      if (isSettingsOpen) {
                        if (settingsStep === 1) {
                          setSettingsStep(0);
                          return;
                        }
                        setIsSettingsOpen(false);
                        return;
                      }

                      navigate({ to: Routes.customer.my_qr_codes.index, preserveQueryParams: true });
                    }}
                  />
                )}
                <Typography variant="xxl" className="font-bold text-xxl">
                  {formTitle}
                </Typography>
              </div>
              {!isBulkMode && (
                <div className="mr-4 relative">
                  <GearIcon className="cursor-pointer hover:text-accept-hover transition-all" onClick={openSettings} />
                </div>
              )}
            </div>
            <FormStepper<EQRCodeStep>
              steps={QRCodeStepperSteps}
              onChange={onChangeActiveStep}
              completedSteps={completedSteps}
              activeStep={activeStep}
              disabled={!canSaveQRCode}
              getStepTitleKey={getStepTitleKey}
              isSecondaryFlow={isSettingsOpen}
            />
          </Container>
          {!isSettingsOpen && (
            <div className="flex flex-1 flex-col gap-4 xl:flex-initial xl:rounded-xl">{renderActiveStep}</div>
          )}

          {isSettingsOpen && (
            <div className="flex flex-col gap-4">
              <QRCodeStepper steps={settingsSteps} activeStep={settingsStep} setActiveStep={setSettingsStep} />
              {settingsStep === 0 && <QRSettingsCurrentTab mutationForUpdate={mutationForUpdate} />}
              {settingsStep === 1 && <QRSettingsGlobalTab />}
            </div>
          )}
        </div>
        <div
          className={clsxm(
            'hidden flex-1 flex-col gap-4 bg-white p-6 font-bold ',
            (activeStep === EQRCodeStep.CUSTOMIZE || showPreviewForVcard) && 'flex min-h-[250px] xl:hidden'
          )}
        >
          <Typography variant="xl" className="text-center">
            {t('qr.preview')}
          </Typography>
          <div className="flex flex-1 items-center justify-center">
            <QRCodePreviewSection activeStep={activeStep} qrId={defaultValues.id} />
          </div>
        </div>
        <Container className="flex w-full flex-col gap-6 xl:w-[35%] xl:min-w-[400px] xl:rounded-xl">
          <div className="sticky top-20 max-h-[calc(100vh-120px)] space-y-6 overflow-auto">
            <div className="hidden flex-col gap-6 font-bold xl:flex">
              <Typography variant="xl">{t('qr.preview')}</Typography>
              <div className="flex min-h-[400px] flex-1 items-center justify-center">
                <QRCodePreviewSection activeStep={activeStep} qrId={defaultValues.id} />
              </div>
            </div>

            {isSettingsOpen && (
              <div className="mb-2 flex justify-between gap-4 xl:flex-col">
                <Button
                  onClick={() => {
                    if (settingsStep === 0) {
                      setSettingsStep(1);
                      return;
                    }
                    setIsSettingsOpen(false);
                  }}
                  disabled={!canSaveQRCode}
                  loading={isLoading || updateLoading || isLoadingBulk}
                  fullWidth
                  endIcon={!isDoneStep && <ChevronRightIcon />}
                >
                  <span>{t('qr.buttonNext')}</span>
                </Button>
                <Button
                  onClick={() => {
                    if (settingsStep === 1) {
                      setSettingsStep(0);
                      return;
                    }
                    setIsSettingsOpen(false);
                  }}
                  variant="outlined"
                  className="-order-1 xl:order-none"
                  fullWidth
                  disabled={isLoading || updateLoading || isLoadingBulk}
                >
                  {t('qr.buttonBack')}
                </Button>
              </div>
            )}
            {!isSettingsOpen && (
              <div className="mb-2 flex justify-between gap-4 xl:flex-col">
                <Button
                  onClick={() => {
                    const currentStepIndex = QRCodeStepperSteps.findIndex((s) => s === activeStep);
                    onChangeActiveStep(QRCodeStepperSteps[currentStepIndex - 1], true);
                  }}
                  variant="outlined"
                  fullWidth
                  disabled={isLoading || updateLoading || isLoadingBulk}
                  className={clsxm('hidden xl:order-2', activeStep !== EQRCodeStep.TYPE && 'inline-flex')}
                >
                  {t('qr.buttonBack')}
                </Button>
                <PlanLimitTooltip condition={!canSaveQRCode && !isBulkMode} divWrap>
                  <Button
                    onClick={async () => {
                      if (activeStep === EQRCodeStep.DONE && isBulkMode && isBulkFillCorrectly) {
                        onSubmitBulk();
                        return;
                      }
                      if (activeStep === EQRCodeStep.INPUT) {
                        if (codeType === ECodeType.GS1) {
                          const isGtinValid = await trigger('gs1.gtin');
                          if (isGtinValid) {
                            const title = formMethods.getValues('gs1.title');
                            const lastEffect = formMethods.getValues('lastCommandEffectRequest');
                            if (!title && !lastEffect) {
                              formMethods.setValue(
                                'lastCommandEffectRequest',
                                COMMAND_EFFECT_REQUEST.GS1_NEED_TO_ENTER_TITLE
                              );
                              return;
                            }
                          }
                        }
                        const isFormValid = await trigger();

                        if (!isFormValid) {
                          return;
                        }
                      }

                      if (activeStep === EQRCodeStep.DONE) {
                        handleSubmit(onSubmitSingle, onErrorSubmit)();
                        return;
                      }

                      const currentStepIndex = QRCodeStepperSteps.findIndex((s) => s === activeStep);
                      onChangeActiveStep(QRCodeStepperSteps[currentStepIndex + 1]);
                    }}
                    disabled={!canSaveQRCode}
                    loading={isLoading || updateLoading || isLoadingBulk}
                    fullWidth
                    className="xl:order-1"
                    endIcon={!isDoneStep && <ChevronRightIcon />}
                  >
                    {isDoneStep ? (
                      isCreateMode ? (
                        <span>
                          {t('qr.buttonConfirm')}{' '}
                          <span className="hidden sm:inline-block">{t('qr.buttonAndSave')}</span>
                        </span>
                      ) : (
                        <span>{t('qr.buttonSaveChanges')}</span>
                      )
                    ) : null}
                    {!isDoneStep && <span>{t('qr.buttonNext')}</span>}
                  </Button>
                </PlanLimitTooltip>
              </div>
            )}
          </div>
        </Container>
      </Form>
    </FormProvider>
  );
};

export const buildFormData = (data: QRCodeFormSchema) => {
  const designValues = pick(data, Object.keys(DesignFormSchema.omit(['template']).fields));
  return {
    codeType: data.codeType,
    name: data.name,
    templateId: data?.templateId,
    password: data?.password || undefined,
    design: !data?.templateId
      ? {
          ...DEFAULT_TEMPLATE_FORM_VALUES,
          ...designValues,
          frameText: designValues?.frameText || '',
          libraryId: designValues?.libraryId === NO_LOGO_ID ? undefined : designValues.libraryId,
        }
      : undefined,
  };
};

export const buildContentDto = (data: QRCodeFormSchema): Partial<QRCodeInDtoOverride> => {
  switch (data.codeType) {
    case ECodeType.WEBSITE:
      return { website: { url: data?.websiteUrl || '' } };

    case ECodeType.MULTILINK: {
      const value = data.multilink;
      if (!value) {
        return {};
      }
      return {
        multilink: {
          ...value,
          customLinks: value.customLinks?.filter((l): l is LinkInDto => Boolean(l.linkType && l.url && l.title)) || [],
          socialLinks:
            value.socialLinks
              ?.filter((l): l is LinkInDto => Boolean(l.linkType && l.url))
              .map((e) => ({
                ...e,
                title: e.title || 'social',
              })) || [],
          libraryId: value.libraryId === NO_LOGO_ID ? undefined : value.libraryId,
          description: value.description || '',
        },
      };
    }

    case ECodeType.PDF:
      return { pdf: { ...data.pdf, fileId: data.pdf?.fileId || '' } };

    case ECodeType.V_CARD: {
      const value = data.vcard;
      if (!value) {
        return {};
      }
      return {
        vcard: {
          ...value,
          links: value.links?.filter((l): l is VCardLinkInDto => Boolean(l.linkType && l.url)),
          libraryId: value.libraryId === NO_LOGO_ID ? undefined : value.libraryId,
          alternativePhoneNumber: value.alternativePhoneNumber ?? undefined,
          email: value.email ?? undefined,
          website: value.website ?? undefined,
          companyName: value.companyName ?? undefined,
          jobPosition: value.jobPosition ?? undefined,
          street: value.street ?? undefined,
          postalCode: value.postalCode ?? undefined,
          city: value.city ?? undefined,
          state: value.state ?? undefined,
          country: value.country ?? undefined,
        },
      };
    }

    case ECodeType.GS1: {
      const value = data.gs1;
      if (!value) {
        return {};
      }
      return {
        gs1: {
          ...value,
          customLinks: value.customLinks?.filter((l): l is LinkInDto => Boolean(l.linkType && l.url && l.title)) || [],
          socialLinks:
            value.socialLinks
              ?.filter((l): l is LinkInDto => Boolean(l.linkType && l.url))
              .map((e) => ({
                ...e,
                title: e.title || 'social',
              })) || [],
          libraryId: value.libraryId === NO_LOGO_ID ? undefined : value.libraryId,
          description: value.description || '',
        },
      };
    }

    case ECodeType.WIFI: {
      const value = data.wifi;
      if (!value) {
        return {};
      }
      return {
        wifi: {
          ...value,
          libraryId: value.libraryId === NO_LOGO_ID ? undefined : value.libraryId,
          password: value.password ?? undefined,
          description: value.description || undefined,
        },
      };
    }

    default:
      throw new Error(`Unsupported codeType: ${data.codeType}`);
  }
};
